import {
  Fragment,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@kizen/kds/Button';
import produce from 'immer';
import Draggable from 'react-draggable';
import ActivityService from 'services/ActivityService';
import { camelToSnakeCaseKeys } from 'services/helpers';
import CustomFieldChooserModal from 'components/Modals/CustomFieldChooser';
import ActivityBuilderPreview from 'components/Modals/ActivityBuilderPreview';
import useModal from 'components/Modals/useModal';

import BuilderField, {
  fieldMargin,
  menuBuildOptions as menuBuildOptionsOrigin,
} from 'components/Builders/CustomFieldsBuilder/Field';
import { FieldPlaceholder } from 'components/Builders/CustomFieldsBuilder/styles';

import {
  HorizontalDropzone,
  VerticalDropzone,
} from 'components/DragAndDropLayout/Dropzone';

import { KizenTypography } from 'app/typography';
import {
  dropzoneIs,
  applyDropzone,
} from 'components/DragAndDropLayout/helpers';
import { isOwnerField } from 'checks/fields';

import { getFieldDropzone } from 'components/Builders/CustomFieldsBuilder/helpers';

import {
  MENU_ACTION_SHOW,
  MENU_ACTION_HIDE,
  MENU_ACTION_REQUIRED,
  MENU_ACTION_OPTIONAL,
  MENU_ACTION_EDIT_OPTIONALS,
  MENU_ACTION_ADD_RULE,
} from 'components/Builders/CustomFieldsBuilder/menuActionOptions';

import { isDefaultUndeletableNotesField } from 'checks/fields';
import { invalidate } from 'queries/invalidate';
import BuilderActivity from './Activity';
import ActivityFieldWizard from './Field';

import {
  BigCard,
  TitleWrapper,
  ActivitiesTitle,
  ActivitiesFooter,
  EditTextBlockModalContent,
  FooterNewFieldTypography,
  FieldLayout,
  ItemWrapper,
  RightHeader,
} from './styles';
import { useCustomFieldChooserWithCache } from 'components/Modals/CustomFieldChooser/hooks';
import useExportFieldMetadata from 'hooks/useExportFieldMetadata';
import BasicModal from 'components/Modals/presets/BasicModal';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { OBJECT_ASSOCATIONS } from 'components/GenericWizard/wizards/scheduleActivity';
import TextInput from 'components/Inputs/TextInput';
import { BasicRichTextEditor } from 'components/WYSIWYG';
import {
  deleteLayoutItemFromVisibilityRules,
  isVisibilityRuleInvalid,
} from 'ts-activities/utils';
import { getOriginalError } from 'services/AxiosService';
import { toastVariant, useToast } from 'components/ToastProvider';
import { flushSync } from 'react-dom';
import UUID from 'utility/UUID';

// we do not show required options for activity Owner field
class menuBuildOptions extends menuBuildOptionsOrigin {
  static required = (field, t) => {
    if (isOwnerField(field)) return [];
    return menuBuildOptionsOrigin.required(field, t);
  };
}

function NewField({ onClick, label, disabled, ...rest }) {
  return (
    <FooterNewFieldTypography
      as="span"
      weight="bold"
      size="buttonLabel"
      textTransform="uppercase"
      onClick={(ev) => {
        if (!disabled) {
          onClick(ev);
        }
      }}
      disabled={disabled}
      {...rest}
    >
      {label}
    </FooterNewFieldTypography>
  );
}

const ACTIVITY_CUSTOM_FIELD = 'activity_custom_field';

const getDefaultSectionText = (t) => {
  return `<p style="line-height: 1.25" data-line-height="default"><span style="font-family: Arial; font-size: 14px">${t('Help Text')}</span></p>`;
};

const getMenuOptionsForLayoutItem = (item, t) => {
  if (item.type === 'section') {
    return [
      { value: 'edit-text-block', label: t('Edit') },
      ...menuBuildOptions.delete({ isDeletable: true }, t),
    ];
  }

  if (isDefaultUndeletableNotesField(item.field)) {
    return menuBuildOptions.visibility(item.field, t, {
      showLabel: t('Activate'),
      hideLabel: t('Deactivate'),
    });
  }

  return [
    ...menuBuildOptions.edit({ ...item.field, isDefault: false }, t),
    ...menuBuildOptions.visibility(item.field, t, {
      showLabel: t('Activate'),
      hideLabel: t('Deactivate'),
    }),
    ...(!item.field.meta.isReadonly
      ? menuBuildOptions.required(item.field, t)
      : []),
    ...menuBuildOptions.addRule(item.field, t),
    ...menuBuildOptions.delete({ ...item.field, isDefault: false }, t),
  ].filter(Boolean);
};

const findFieldInActivity = ({ fields }, fieldId) => {
  return fields.find((field) => field.id === fieldId);
};

const moveFieldToBottom = (layout, fieldId) => {
  const result = [];
  let last = null;

  for (const item of layout) {
    if (item.fieldId === fieldId) {
      last = item;
    } else {
      result.push(item);
    }
  }

  return last ? result.concat(last) : result;
};

const getLayoutForAPI = (layout) => {
  return layout.map((item) => {
    const { id, ...data } = item;

    if ('internalName' in data) {
      const { internalName, ...rest } = data;
      return { ...rest, internal_name: internalName };
    }

    return data;
  });
};

const getInternalNameValidator = (layout, t) => {
  return (internalName, index) => {
    for (const [idx, item] of layout.entries()) {
      if (item.type === 'field' && item.fieldId === internalName) {
        // unlikely, but since layout items don't have an id we want to
        // guarantee items are unique across 'field_id' and 'internal_name'
        return t('Field identifiers cannot be used as internal names');
      }
      if (index === idx) {
        continue;
      }
      if (item.type === 'section' && item.internalName === internalName) {
        return t('Internal name already exists');
      }
    }

    return null;
  };
};

// dynamic tags expect a model and a fetch url, I've asked Scott where the tags should go,
// but this stops things crashing for now
const activityToModel = (activity) => {
  const { fields, name, ...others } = activity;
  return {
    ...others,
    name,
    description: 'Submissions',
    fetchUrl: '',
    noLink: true,
    hideCount: true,
  };
};

function ActivitiesBuilder({
  activity,
  setActivity,
  textBlockInternalNames,
  setTextBlockInternalNames,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [fieldDragging, setFieldDragging] = useState(null);
  const [fieldDropzone, setFieldDropzone] = useState(null);
  const [showToast] = useToast();
  const [wizardData, setWizardData] = useState({});
  const [stagedDisplayNamesById, setStagedDisplayNamesById] = useState({});
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  const [editingTextBlock, setEditingTextBlock] = useState(null);
  const [addingTextBlock, setAddingTextBlock] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const { handleFieldExport } = useExportFieldMetadata({ activity });
  const timeoutIdRef = useRef(null);

  useEffect(() => {
    if (fieldDragging) {
      document.body.style.cursor = 'grabbing';
    } else {
      document.body.style.cursor = 'default';
    }
  }, [fieldDragging]);

  const customActivityFields = useMemo(() => {
    return (
      activity.fields?.filter(
        ({ fieldType }) => fieldType === ACTIVITY_CUSTOM_FIELD
      ) ?? []
    );
  }, [activity]);

  const needsInitialization =
    customActivityFields.length > 0 &&
    Object.keys(stagedDisplayNamesById).length === 0;

  useEffect(() => {
    if (needsInitialization) {
      const intialDisplayNamesById = customActivityFields.reduce(
        (acc, field) => {
          acc[field?.id] = field.displayName;
          return acc;
        },
        {}
      );
      setStagedDisplayNamesById(intialDisplayNamesById);
    }
  }, [needsInitialization, customActivityFields]);

  const customFieldChooserProps = useCustomFieldChooserWithCache(
    (categories, field) => {
      return categories.reduce((acc, category) => {
        const options = category.options.filter(
          ({ item: { isReadOnly, id } }) => {
            return (
              !isReadOnly &&
              (field ||
                !activity.fields?.some(
                  ({ customObjectField }) => customObjectField?.id === id
                ))
            );
          }
        );

        if (!options.length) {
          return acc;
        }

        acc.push({ ...category, options });

        return acc;
      }, []);
    }
  );
  const applyFieldDropzone = async () => {
    if (!fieldDragging || !fieldDropzone) {
      return;
    }
    const nextActivity = produce(activity, (draft) => {
      const [fromFields] = applyDropzone(
        fieldDragging,
        [draft.layout],
        fieldDropzone
      );
      draft.layout = fromFields;
    });

    // Optimistically display the drop
    setActivity(nextActivity);
    await ActivityService.v2UpdateActivity({
      id: nextActivity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    });
  };

  const handleAddSection = async (internalName) => {
    const section = {
      columns: 2,
      type: 'section',
      label: getDefaultSectionText(t),
      internalName,
      id: UUID.generate(),
    };
    const nextActivity = produce(activity, (draft) => {
      draft.layout.push(section);
    });

    setActivity(nextActivity);
    setTextBlockInternalNames((prev) => {
      return { ...prev, [section.id]: internalName };
    });

    await ActivityService.v2UpdateActivity({
      id: activity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    });
  };

  const handleDeleteSection = async (id) => {
    let deletedSection = null;

    const nextActivity = produce(activity, (draft) => {
      const index = draft.layout.findIndex((x) => x.id === id);
      deletedSection = { ...draft.layout[index] };
      draft.layout.splice(index, 1);
    });

    setActivity(nextActivity);

    await ActivityService.v2UpdateActivity({
      id: activity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    });

    if (deletedSection) {
      updateVisibilityRulesAfterDeleteOrDeactivate({
        internal_name: deletedSection.internalName,
      });
    }
  };

  const updateSection = async (id, { label, internalName }) => {
    const nextActivity = produce(activity, (draft) => {
      const item = draft.layout.find((x) => x.id === id);

      if (item) {
        if (label) {
          item.label = label;
        }

        if (internalName) {
          item.internalName = internalName;
        }
      }
    });

    setActivity(nextActivity);

    await ActivityService.v2UpdateActivity({
      id: activity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    });
  };

  const updateFieldStore = useCallback(
    async (field) => {
      const { id } = field;
      const nextActivity = produce(activity, (draft) => {
        const f = findFieldInActivity(draft, id);

        if (f) {
          // update the two fields & options
          f.isRequired = field.isRequired;
          f.displayName = field.displayName;
          f.options = field.options;
          f.meta = field.meta;

          if (f.fieldType === 'relationship') {
            f.relation = field.relation;
          }
        } else {
          draft.fields.push(field);
          draft.layout.push({ columns: 1, id, fieldId: id, type: 'field' });
        }
      });

      setActivity(nextActivity, nextActivity.fields);

      await ActivityService.v2UpdateActivity({
        id: nextActivity.id,
        layout: getLayoutForAPI(nextActivity.layout),
      });
    },
    [activity, setActivity]
  );

  const [activityFieldWizardModalProps, , activityFieldWizardModal] = useModal({
    handleSubmit: (field) => {
      updateFieldStore(field);

      if (field.fieldType === 'relationship') {
        queryClient.invalidateQueries({
          queryKey: [
            'related_objects',
            field.relation.relatedObject,
            field.id,
            { filter_activity_field_id: field.id },
          ],
        });
      }
    },
  });

  const handleFieldEditOptions = async (field) => {
    setWizardData({
      activityObjectId: activity.id,
      field,
      model: activityToModel(activity), // the activity is the model in this case as far as the wizard is concerned
    });
    activityFieldWizardModal.show();
  };

  const handleNewActivityField = () => {
    setWizardData({
      activityObjectId: activity.id,
      model: activityToModel(activity), // the activity is the model in this case as far as the wizard id concerned
    });

    activityFieldWizardModal.show();
  };

  const updateVisibilityRulesAfterDeleteOrDeactivate = async (
    item,
    isDelete,
    nextActivity
  ) => {
    const updatedVisibilityRules = Array.isArray(activity.visibilityRules)
      ? deleteLayoutItemFromVisibilityRules(
          camelToSnakeCaseKeys(activity.visibilityRules),
          item
        )
      : [];
    const invalidChecks = updatedVisibilityRules.map(isVisibilityRuleInvalid);
    const firstInvalidRule = invalidChecks.findIndex((x) => x === true);

    if (firstInvalidRule > -1) {
      const error_message = isDelete
        ? t('The field has been deleted')
        : t('The field has been deactivated');
      const invalidCount = invalidChecks.filter((x) => x === true).length;
      const withErrorMessages = updatedVisibilityRules.map((vr) => {
        if (vr.rule.filters.length) return vr;
        const filter = {
          view_model: [['field_id', { error: true, error_message }]],
        };
        return { ...vr, rule: { ...vr.rule, filters: [filter] } };
      });
      history.push(`/activities/${activity.id}/rules`, {
        visibilityRules: withErrorMessages,
        scrollCardIndex: firstInvalidRule,
        errorMessage: t(
          'You have {{count}} invalid {{rules}} after {{action}} your {{item}}. Please update your rules before leaving activity settings.',
          {
            count: invalidCount,
            rules: invalidCount > 1 ? t('rules') : t('rule'),
            action: isDelete ? t('deleting') : t('deactivating'),
            item: 'internal_name' in item ? t('text block') : t('field'),
          }
        ),
      });
    } else {
      const res = await ActivityService.v2UpdateActivity({
        id: activity.id,
        visibilityRules: updatedVisibilityRules,
      });
      invalidate.ACTIVITIES.ACTIVITY(activity.id);

      if (nextActivity) {
        setActivity({ ...nextActivity, visibilityRules: res.visibilityRules });
      } else {
        setActivity((prev) => ({
          ...prev,
          visibilityRules: res.visibilityRules,
        }));
      }
    }
  };

  const handleFieldDelete = async ({ id }) => {
    const nextActivity = produce(activity, (draft) => {
      const f = draft.fields.filter((field) => field.id !== id);
      if (f) {
        draft.fields = f;
        draft.layout = draft.layout.filter(
          (x) => x.type === 'section' || x.fieldId !== id
        );
      }
    });
    setActivity(nextActivity);

    await ActivityService.v2DeleteField({
      activityObjectId: nextActivity.id,
      id,
    });

    if (activity.visibilityRules.length) {
      updateVisibilityRulesAfterDeleteOrDeactivate({ field_id: id }, true);
    }
  };

  const handleResize = async (cols, { id }) => {
    const nextActivity = produce(activity, (draft) => {
      const item = draft.layout.find((x) => x.id === id);
      item.columns = cols;
    });
    setActivity(nextActivity);

    await ActivityService.v2UpdateActivity({
      id: nextActivity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    });
  };
  const handleNewCustomField = () => {
    setWizardData({
      title: t('Add Custom Field'),
      displayNamePrefix: 'Activity',
      activityObjectId: activity.id,
      activityCustomObjects: activity.customObjects,
    });
    setShowCustomFieldModal(true);
    return true;
  };

  const handleEditCustomField = (field) => {
    setWizardData({
      title: t('Edit Custom Field'),
      displayNamePrefix: 'Activity',
      field,
      activityObjectId: activity.id,
    });
    setShowCustomFieldModal(true);
    return true;
  };

  const handleFieldSelectAction = async ({ value: action }, field) => {
    if (
      action === MENU_ACTION_SHOW ||
      action === MENU_ACTION_HIDE ||
      action === MENU_ACTION_REQUIRED ||
      action === MENU_ACTION_OPTIONAL
    ) {
      const { id } = field;
      let updateFields = false;
      let updateActivityLayout = false;

      const nextActivity = produce(activity, (draft) => {
        const f = findFieldInActivity(draft, id);
        if (f) {
          switch (action) {
            case MENU_ACTION_HIDE:
              f.isHidden = true;
              f.isRequired = false;
              updateFields = true;
              updateActivityLayout = true;
              draft.layout = moveFieldToBottom(draft.layout, id);
              break;

            case MENU_ACTION_SHOW:
              f.isHidden = false;
              break;

            case MENU_ACTION_REQUIRED:
              f.isRequired = true;
              break;

            case MENU_ACTION_OPTIONAL:
              f.isRequired = false;
              break;

            default:
              break;
          }
        }
      });

      setActivity(nextActivity, nextActivity.fields);

      const f = findFieldInActivity(nextActivity, id);
      if (f) {
        const { isHidden, isRequired } = f;
        const { id: activityObjectId } = nextActivity;
        const payload = {
          id,
          activityObjectId,
          isHidden,
          isRequired,
        };

        const promises = [ActivityService.v2UpdateField(payload)];

        if (updateActivityLayout) {
          promises.push(
            ActivityService.v2UpdateActivity({
              id: activity.id,
              layout: getLayoutForAPI(nextActivity.layout),
            })
          );
        }

        await Promise.all(promises);

        if (updateFields && activity.visibilityRules.length) {
          updateVisibilityRulesAfterDeleteOrDeactivate(
            { field_id: id },
            false,
            nextActivity
          );
        }
      }
    } else if (action === MENU_ACTION_EDIT_OPTIONALS) {
      if (field.fieldType === ACTIVITY_CUSTOM_FIELD) {
        // custom field
        handleEditCustomField(field);
      } else {
        // activity field
        handleFieldEditOptions(field);
      }
    } else if (action === MENU_ACTION_ADD_RULE) {
      history.push(`/activities/${activity.id}/rules`, {
        addRuleField: camelToSnakeCaseKeys(field),
      });
    } else if (action === 'edit-text-block') {
      setEditingTextBlock({
        ...field,
        initialLabel: field.label,
        editingInternalName: field.internalName,
      });
    }
  };

  const handleChangeFieldDescription = async (
    displayName,
    { id, displayName: prev }
  ) => {
    if (displayName === prev) {
      return;
    }

    if (displayName === '') {
      // throw an error so the stagedDescription is reset
      throw new Error(`Field name can't be blank`);
    }

    const { id: activityObjectId } = activity;
    const payload = {
      activityObjectId,
      id,
      displayName,
    };

    const nextActivity = produce(activity, (draft) => {
      const f = findFieldInActivity(draft, id);
      if (f) {
        f.displayName = displayName;
      }
    });
    setActivity(nextActivity, nextActivity.fields);

    await ActivityService.v2UpdateField(payload);
  };

  const handleNewCustomFieldSave = useCallback(
    async (field) => {
      // todo back out adding fieldType to meta once api returns model_field
      const { id: activityObjectId } = activity;
      try {
        let payload;
        const { orgField } = field;
        if (!orgField) {
          const {
            id: customObjectField,
            isRequired,
            isDefault,
            displayName,
            description,
            descriptionVisibility,
            meta,
          } = field;
          payload = {
            activityObjectId,
            fieldType: ACTIVITY_CUSTOM_FIELD,
            isDefault,
            description,
            descriptionVisibility,
            displayName,
            customObjectField,
            isRequired,
            meta: { ...meta, cols: 1 },
          };
          payload = await ActivityService.v2CreateField(payload);
        } else {
          const {
            isRequired,
            displayName,
            meta,
            name,
            description,
            descriptionVisibility,
          } = field;
          const { id } = orgField;
          payload = {
            activityObjectId,
            id,
            displayName,
            description,
            descriptionVisibility,
            isRequired,
            meta,
            name,
          };
          payload = await ActivityService.v2UpdateField(payload);
        }
        updateFieldStore(payload);
        return { success: true, error: null };
      } catch (error) {
        const orig = getOriginalError(error);
        if (orig?.message) {
          showToast({
            variant: toastVariant.FAILURE,
            message: orig.message,
          });
          setShowCustomFieldModal(true);
        }
        return { success: false, error: orig };
      }
    },
    [activity, showToast, updateFieldStore]
  );

  const ruleIndexesForLayoutItem = useMemo(() => {
    const init = { hidden: {}, conditions: {} };

    if (!activity.visibilityRules) {
      return init;
    }

    return activity.visibilityRules.reduce((acc, vr, idx) => {
      for (const item of vr.layoutItems) {
        const id = item.fieldId || item.internalName;

        if (acc.hidden[id] === undefined) {
          acc.hidden[id] = idx;
        }
      }
      for (const cond of vr.rule.filters) {
        if (acc.conditions[cond.activityFieldId] === undefined) {
          acc.conditions[cond.activityFieldId] = idx;
        }
      }
      return acc;
    }, init);
  }, [activity.visibilityRules]);

  const handleCustomFieldSave = useCallback(
    async (field, originalId) => {
      const response = handleNewCustomFieldSave(field); // returns promise that resolves to { success: boolean, error: any }
      const id = originalId || field.id;
      setStagedDisplayNamesById((prev) => {
        return {
          ...prev,
          [id]: field.displayName,
        };
      });
      return response;
    },
    [handleNewCustomFieldSave]
  );

  const layoutData = useMemo(() => {
    const visible = [];
    const hidden = [];
    const fieldLookup = activity.fields.reduce((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});

    for (const item of activity.layout) {
      if (item.type === 'field' && !fieldLookup[item.fieldId]) {
        continue;
      }

      if (item.type === 'field' && fieldLookup[item.fieldId].isHidden) {
        hidden.push({ ...item, field: fieldLookup[item.fieldId] });
      } else {
        if (item.type === 'field') {
          visible.push({ ...item, field: fieldLookup[item.fieldId] });
        } else {
          visible.push(item);
        }
      }
    }

    return visible.concat(hidden);
  }, [activity]);

  const validateInternalName = (index) => (internalName) => {
    const validator = getInternalNameValidator(activity.layout, t);
    return validator(internalName, index);
  };

  let x = 0;
  let endOfLine = true;

  return (
    <>
      <BigCard data-qa="activity-builder-modal">
        <ActivitiesTitle as={TitleWrapper}>
          <KizenTypography as="h3" type="subheader">
            {t('Activity Builder')}
          </KizenTypography>
          <RightHeader>
            <Button
              className="pb-[10px]"
              color="secondary"
              onClick={handleFieldExport}
              variant="text"
            >
              {t('Export Field Metadata')}
            </Button>
          </RightHeader>
        </ActivitiesTitle>

        <BuilderActivity
          onMouseMove={(ev) => {
            if (fieldDragging) {
              setFieldDropzone(
                getFieldDropzone(
                  fieldDragging,
                  { id: activity.id, fields: activity.layout },
                  ev
                )
              );
            }
          }}
        >
          <FieldLayout>
            <>
              {dropzoneIs(fieldDropzone, {
                sectionId: activity.id,
                position: 'first',
              }) && <HorizontalDropzone margin={fieldMargin} />}
            </>
            {layoutData.map((item, i, arr) => {
              const isTopDropZone = dropzoneIs(fieldDropzone, {
                id: item.id,
                position: 'before',
                direction: 'horizontal',
              });
              const isLeftDropZone = dropzoneIs(fieldDropzone, {
                id: item.id,
                position: 'before',
                direction: 'vertical',
              });
              const isRightDropZone = dropzoneIs(fieldDropzone, {
                id: item.id,
                position: 'after',
                direction: 'vertical',
              });
              const isBottomDropZone = dropzoneIs(fieldDropzone, {
                id: item.id,
                position: 'after',
                direction: 'horizontal',
              });
              const wasEndOfLine = endOfLine;
              const cols = item.columns;
              const nextlayoutItem = arr[i + 1];
              const nextCols = nextlayoutItem ? nextlayoutItem.columns : 0;
              endOfLine = x + cols >= 2 || x + cols + nextCols > 2;
              x = endOfLine ? 0 : 1;

              if (item.type === 'field') {
                const isNotesField = isDefaultUndeletableNotesField(item.field);
                const menuOptions = getMenuOptionsForLayoutItem(item, t);
                const f = { ...item.field };
                if (f && isOwnerField(f)) {
                  f.isRequired = true;
                }

                return (
                  <Fragment key={item.id}>
                    {wasEndOfLine && isTopDropZone && (
                      <HorizontalDropzone margin={fieldMargin} />
                    )}
                    <ItemWrapper
                      data-qa="builder-field"
                      key={f.id}
                      className="FieldItemWrapper"
                      cols={2}
                      width={cols}
                      dragging={fieldDragging && fieldDragging.id === f.id}
                    >
                      {isLeftDropZone && (
                        <VerticalDropzone margin={fieldMargin} />
                      )}
                      <FieldPlaceholder />
                      <Draggable
                        disabled={f.isHidden || !activity.id}
                        onStart={() => flushSync(() => setFieldDragging(item))}
                        onStop={() => {
                          flushSync(() => {
                            applyFieldDropzone();
                            setFieldDragging(null);
                            setFieldDropzone(null);
                          });
                        }}
                        position={fieldDragging ? null : { x: 0, y: 0 }}
                        handle=".FieldHandle"
                      >
                        <BuilderField
                          field={f}
                          columns={item.columns}
                          handleProps={{ className: 'FieldHandle' }}
                          dragging={fieldDragging && fieldDragging.id === f.id}
                          onChangeDescription={handleChangeFieldDescription}
                          onConfirmDelete={handleFieldDelete}
                          onClickChangeColumnWidth={handleResize}
                          menuOptions={menuOptions}
                          isReadonly={item.field.meta.isReadonly}
                          isDisabled={isNotesField || !activity.id}
                          onLabelChange={(id, updatedLabel) =>
                            setStagedDisplayNamesById((prev) => {
                              return {
                                ...prev,
                                [id]: updatedLabel,
                              };
                            })
                          }
                          label={
                            stagedDisplayNamesById[f.id] !== undefined
                              ? stagedDisplayNamesById[f.id]
                              : f.displayName
                          }
                          onSelectAction={handleFieldSelectAction}
                          disableChangeColumnWidth={isNotesField}
                          confirmDeleteMessage={t(
                            'This will permanently delete this field and all historical submission data from this activity.'
                          )}
                          canDeleteFields
                          isHiddenByRule={
                            ruleIndexesForLayoutItem.hidden[f.id] !== undefined
                          }
                          isRuleCondition={
                            ruleIndexesForLayoutItem.conditions[f.id] !==
                            undefined
                          }
                          onHiddenByRuleClick={() =>
                            history.push(`/activities/${activity.id}/rules`, {
                              scrollCardIndex:
                                ruleIndexesForLayoutItem.hidden[f.id],
                            })
                          }
                          onRuleConditionClick={() =>
                            history.push(`/activities/${activity.id}/rules`, {
                              scrollCardIndex:
                                ruleIndexesForLayoutItem.conditions[f.id],
                            })
                          }
                        />
                      </Draggable>
                      {isRightDropZone && (
                        <VerticalDropzone margin={fieldMargin} />
                      )}
                    </ItemWrapper>
                    {(endOfLine || i === arr.length - 1) &&
                      isBottomDropZone && (
                        <HorizontalDropzone margin={fieldMargin} />
                      )}
                  </Fragment>
                );
              }

              const menuOptions = getMenuOptionsForLayoutItem(item, t);

              return (
                <Fragment key={item.id}>
                  {wasEndOfLine && isTopDropZone && (
                    <HorizontalDropzone margin={fieldMargin} />
                  )}
                  <ItemWrapper
                    data-qa="builder-field"
                    key={item.id}
                    className="FieldItemWrapper"
                    cols={2}
                    width={cols}
                    dragging={fieldDragging && fieldDragging.id === item.id}
                  >
                    {isLeftDropZone && (
                      <VerticalDropzone margin={fieldMargin} />
                    )}
                    <FieldPlaceholder />
                    <Draggable
                      disabled={!activity.id}
                      onStart={() => flushSync(() => setFieldDragging(item))}
                      onStop={() => {
                        flushSync(() => {
                          applyFieldDropzone();
                          setFieldDragging(null);
                          setFieldDropzone(null);
                        });
                      }}
                      position={fieldDragging ? null : { x: 0, y: 0 }}
                      handle=".FieldHandle"
                    >
                      <BuilderField
                        canDeleteFields
                        field={item}
                        columns={item.columns}
                        leftIconName="text-size-light"
                        handleProps={{ className: 'FieldHandle' }}
                        dragging={fieldDragging && fieldDragging.id === item.id}
                        validator={validateInternalName(i)}
                        onChangeDescription={() => {
                          updateSection(item.id, {
                            internalName: textBlockInternalNames[item.id],
                          });
                        }}
                        onConfirmDelete={() => handleDeleteSection(item.id)}
                        onClickChangeColumnWidth={handleResize}
                        menuOptions={menuOptions}
                        isDisabled={!activity.id}
                        onLabelChange={(id, updatedLabel) => {
                          setTextBlockInternalNames((prev) => {
                            return { ...prev, [id]: updatedLabel };
                          });
                        }}
                        label={textBlockInternalNames[item.id]}
                        onSelectAction={handleFieldSelectAction}
                        confirmDeleteMessage={t(
                          'This will permanently delete the text block.'
                        )}
                        isHiddenByRule={
                          ruleIndexesForLayoutItem.hidden[item.internalName] !==
                          undefined
                        }
                        onHiddenByRuleClick={() =>
                          history.push(`/activities/${activity.id}/rules`, {
                            scrollCardIndex:
                              ruleIndexesForLayoutItem.hidden[
                                item.internalName
                              ],
                          })
                        }
                      />
                    </Draggable>
                    {isRightDropZone && (
                      <VerticalDropzone margin={fieldMargin} />
                    )}
                  </ItemWrapper>
                  {(endOfLine || i === arr.length - 1) && isBottomDropZone && (
                    <HorizontalDropzone margin={fieldMargin} />
                  )}
                </Fragment>
              );
            })}
          </FieldLayout>
        </BuilderActivity>

        <ActivitiesFooter>
          <NewField
            data-qa="add-new-custom-field-button"
            onClick={handleNewCustomField}
            label={t('+ add new custom field')}
            disabled={!activity.id}
          />
          <NewField
            data-qa="add-new-activity-field-button"
            onClick={handleNewActivityField}
            label={t('+ add new activity field')}
            disabled={!activity.id}
          />
          <NewField
            onClick={() => setAddingTextBlock({ internalName: '' })}
            label={t('+ add new text block')}
            disabled={!activity.id}
          />
        </ActivitiesFooter>

        <ActivityFieldWizard
          context={wizardData}
          {...activityFieldWizardModalProps}
        />

        {editingTextBlock && (
          <>
            {editingTextBlock.showConfirmExit && (
              <ConfirmationModal
                show
                heading={t('You Have Unsaved Changes')}
                buttonText={t('Discard Changes')}
                actionBtnColor="red"
                onConfirm={() => {
                  setEditingTextBlock(null);
                }}
                onHide={() => {
                  setEditingTextBlock((prev) => {
                    const { showConfirmExit, ...rest } = prev;
                    return rest;
                  });
                }}
              >
                {t('Unsaved changes will be lost, would you like to continue?')}
              </ConfirmationModal>
            )}
            <BasicModal
              show
              size="medium"
              heading={t('Edit Text Block')}
              leftBtn={null}
              onHide={() => {
                if (
                  editingTextBlock.label !== editingTextBlock.initialLabel ||
                  editingTextBlock.internalName !==
                    editingTextBlock.editingInternalName
                ) {
                  setEditingTextBlock((prev) => {
                    return { ...prev, showConfirmExit: true };
                  });
                } else {
                  setEditingTextBlock(null);
                }

                if (timeoutIdRef.current !== null) {
                  clearTimeout(timeoutIdRef.current);
                  timeoutIdRef.current = null;
                }
              }}
              onConfirm={async () => {
                if (editingTextBlock.validate) {
                  return;
                }

                if (editingTextBlock.errorMessage) {
                  setEditingTextBlock((prev) => {
                    return {
                      ...prev,
                      validate: {
                        showMessage: true,
                        message: editingTextBlock.errorMessage,
                      },
                    };
                  });
                  timeoutIdRef.current = setTimeout(() => {
                    setEditingTextBlock((prev) => {
                      const { validate, ...rest } = prev;
                      return rest;
                    });
                    timeoutIdRef.current = null;
                  }, 3000);
                } else {
                  setEditingTextBlock(null);

                  await updateSection(editingTextBlock.id, {
                    label: editingTextBlock.label,
                    internalName: editingTextBlock.internalName,
                  });

                  setTextBlockInternalNames((prev) => {
                    return {
                      ...prev,
                      [editingTextBlock.id]: editingTextBlock.internalName,
                    };
                  });
                }
              }}
            >
              <EditTextBlockModalContent>
                <TextInput
                  value={editingTextBlock.editingInternalName}
                  label={t('Internal Name')}
                  placeholder={t('Enter Internal Name')}
                  validate={editingTextBlock.validate}
                  onChange={(value) => {
                    if (timeoutIdRef.current) {
                      clearTimeout(timeoutIdRef.current);
                      timeoutIdRef.current = null;
                    }

                    setEditingTextBlock((prev) => {
                      const { validate, errorMessage, ...rest } = prev;
                      return { ...rest, editingInternalName: value };
                    });
                  }}
                  onBlur={() => {
                    const idx = layoutData.findIndex(
                      (item) => item.id === editingTextBlock.id
                    );

                    const message = !editingTextBlock.editingInternalName.trim()
                      ? t('This field is required')
                      : validateInternalName(idx)(
                          editingTextBlock.editingInternalName
                        );

                    if (message) {
                      setEditingTextBlock((prev) => {
                        return {
                          ...prev,
                          errorMessage: message,
                          validate: { showMessage: true, message },
                        };
                      });
                      timeoutIdRef.current = setTimeout(() => {
                        setEditingTextBlock((prev) => {
                          const { validate, ...rest } = prev;
                          return rest;
                        });
                        timeoutIdRef.current = null;
                      }, 3000);
                    } else {
                      setEditingTextBlock((prev) => {
                        const { errorMessage, ...rest } = prev;
                        return {
                          ...rest,
                          internalName: prev.editingInternalName,
                        };
                      });
                    }
                  }}
                />
                <BasicRichTextEditor
                  enableFontSize
                  enableLineHeight
                  enableClearFormatting
                  enableHorizontalRule
                  initialValue={editingTextBlock.label}
                  onChange={({ editor }) => {
                    const label = editor.getHTML();

                    setEditingTextBlock((prev) => {
                      return { ...prev, label };
                    });
                  }}
                />
              </EditTextBlockModalContent>
            </BasicModal>
          </>
        )}
        {addingTextBlock && (
          <BasicModal
            show
            heading={t('Add Text Block')}
            disabled={!addingTextBlock.internalName}
            onHide={() => {
              setAddingTextBlock(null);

              if (timeoutIdRef.current !== null) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
              }
            }}
            onConfirm={() => {
              const message = validateInternalName(activity.layout.length)(
                addingTextBlock.internalName
              );

              if (message) {
                setAddingTextBlock((prev) => {
                  return { ...prev, validate: { showMessage: true, message } };
                });
                timeoutIdRef.current = setTimeout(() => {
                  setAddingTextBlock((prev) => {
                    const { validate, ...rest } = prev;
                    return rest;
                  });
                  timeoutIdRef.current = null;
                }, 3000);
              } else {
                handleAddSection(addingTextBlock.internalName);
                setAddingTextBlock(null);
              }
            }}
          >
            <TextInput
              inModal
              label={t('Internal Name')}
              placeholder={t('Enter Internal Name')}
              value={addingTextBlock.internalName}
              validate={addingTextBlock.validate}
              onChange={(value) => {
                setAddingTextBlock({ internalName: value });
              }}
            />
          </BasicModal>
        )}
        {showCustomFieldModal && (
          <CustomFieldChooserModal
            show
            enableReadonlyToggle
            context={wizardData}
            onHide={() => setShowCustomFieldModal(false)}
            onSave={handleCustomFieldSave}
            label={
              stagedDisplayNamesById?.[wizardData?.field?.id] ??
              wizardData?.field?.displayName
            }
            isActivityField
            notice={
              activity?.associationMode ===
              OBJECT_ASSOCATIONS.NO_OBJECT_ASSOCIATIONS
                ? t(
                    "Since this activity has no associations, this field won't pre-populate or update records"
                  )
                : null
            }
            {...customFieldChooserProps}
          />
        )}

        {showPreview && (
          <ActivityBuilderPreview
            activity={activity}
            onHide={() => {
              setShowPreview(false);
            }}
          />
        )}
      </BigCard>
    </>
  );
}

export default ActivitiesBuilder;
